export const en = {
  app: { name: 'Streamline Engineering' },
  lang: { ro: 'Română', en: 'English', label: 'Language' },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    sessionEnded: 'Your session has ended.',
    signingIn: 'Signing in…',
  },
  roles: { admin: 'Admin', user: 'User' },
  nav: {
    myServices: 'My services',
    search: 'Search',
    tenantAdmin: 'Tenant administration',
    dashboard: 'Dashboard',
    members: 'Members',
    forms: 'Forms',
    services: 'Services',
    instances: 'Service instances',
    templates: 'Output templates',
    audit: 'Audit log',
    platform: 'Platform',
    tenants: 'Tenants',
    catalog: 'Default catalog',
    globalAudit: 'Global audit',
    import: 'Import',
  },
  common: {
    results: '{{count}} result(s)',
    nothingHere: 'Nothing here yet.',
    loading: 'Loading…',
    cancel: 'Cancel',
    create: 'Create',
    start: 'Start',
    submit: 'Submit',
    update: 'Update',
    download: 'Download',
    saving: 'Saving…',
    savedDraft: 'Draft saved',
    unsaved: 'Unsaved changes',
    somethingWrong: 'Something went wrong',
    codeLabel: 'Code: {{code}}',
    name: 'Name',
    status: 'Status',
    updated: 'Updated',
    origin: 'Origin',
    type: 'Type',
    version: 'Version',
    email: 'Email',
    role: 'Role',
    title: 'Title',
    reference: 'Reference',
    created: 'Created',
    joined: 'Joined',
    tenant: 'Tenant',
    platformDefault: 'Platform default',
    service: 'Service',
    copied: 'Copied',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Archived',
    actions: 'Actions',
  },
  status: {
    ACTIVE: 'active',
    DRAFT: 'draft',
    PUBLISHED: 'published',
    ARCHIVED: 'archived',
    SUSPENDED: 'suspended',
    OPEN: 'open',
    IN_PROGRESS: 'in progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    SUBMITTED: 'submitted',
    VALIDATED: 'validated',
    REJECTED: 'rejected',
    EXPIRED: 'expired',
    REVOKED: 'revoked',
    QUEUED: 'queued',
    PROCESSING: 'processing',
    FAILED: 'failed',
    CONSUMED: 'consumed',
    PENDING: 'pending',
    INDEXED: 'indexed',
  },
  myServices: {
    title: 'My services',
    startService: 'Start a service',
    filterPlaceholder: 'Filter by title or reference',
    all: 'All',
    open: 'Open',
    active: 'Active',
    completed: 'Completed',
    reference: 'Reference',
    titleCol: 'Title',
    statusCol: 'Status',
    created: 'Created',
    completedCol: 'Completed',
    empty: 'Nothing here yet — use “Start a service” to open your first case.',
    startTitle: 'Start a service',
    chooseService: 'Choose a service',
    caseTitle: 'Give your case a title',
    noServices: 'No published services available',
  },
  instance: {
    forms: 'Forms',
    shareLinks: 'Share links',
    documents: 'Documents',
    markCompleted: 'Mark completed',
    cancelService: 'Cancel service',
    createdOn: 'created {{date}}',
    completedOn: 'completed {{date}}',
    submitted: 'submitted {{date}}',
    required: 'required',
    externalNote: 'This form is completed externally through a share link.',
    validate: 'Validate',
    reject: 'Reject',
    serviceCompletedTitle: 'Service completed',
    serviceCancelledTitle: 'Service cancelled',
    reopenedTitle: 'Reopened for editing',
    reopenedMessage: 'Forms are editable again, updated to the latest version.',
    reopenForEditing: 'Reopen for editing',
    archive: 'Archive case',
    edit: 'Edit',
    unarchive: 'Unarchive',
    archivedTitle: 'Case archived',
    unarchivedTitle: 'Case unarchived',
    comments: 'Comments',
    compositionUnavailable: 'Service composition unavailable.',
    outputTemplate: 'Output template',
    noTemplatesYet: 'No templates defined yet',
    chooseTemplate: 'Choose a template',
    generateDocument: 'Generate document',
    colTemplate: 'Template',
    colStatus: 'Status',
    colRequested: 'Requested',
    colCompleted: 'Completed',
    noDocumentsYet: 'No documents generated yet.',
    draftConflictTitle: 'Draft changed elsewhere',
    draftConflictMessage: 'This draft was updated in another window. Reload to continue.',
    fixHighlighted: 'Please fix the highlighted fields.',
    submittedTitle: 'Submitted',
    submittedMessage: '{{name}} was submitted.',
    completionMode_INTERNAL: 'Internal',
    completionMode_EXTERNAL: 'External',
    completionMode_BOTH: 'Internal & external',
    noExternalForms: 'No externally completable forms in this service.',
    colForm: 'Form',
    linkRecipientHint: 'The recipient can reopen the link to update their answers',
    createShareLink: 'Create share link',
    linkCreatedTitle: 'Link created — copy it now, it is shown only once',
    copy: 'Copy',
    copied: 'Copied',
    colSubmissions: 'Submissions',
    colExpires: 'Expires',
    colCreated: 'Created',
    revoke: 'Revoke',
    noShareLinksYet: 'No share links yet.',
  },
  publicForm: {
    progressSaved: 'Progress saved automatically',
    thankYou: 'Thank you!',
    submittedOk: 'Your form was submitted successfully.',
    confirmation: 'Confirmation: {{id}}',
    unavailable: 'Unavailable',
    linkInvalid: 'This link is not valid.',
    linkExpired: 'This link has expired. Please ask your contact for a new one.',
    linkRevoked: 'This link is no longer available.',
    linkConsumed: 'This form has already been submitted.',
    alreadySubmitted: 'You have already submitted this form — you can update it below.',
    saved: 'Saved.',
    noLongerAvailable: 'This form is no longer available.',
    fixHighlighted: 'Please fix the highlighted fields.',
    uploadFailed: 'Upload failed',
  },
  publicService: {
    intro: 'Complete the following form(s) to submit your request. Click start when you are ready.',
    saveContinue: 'Save & continue',
    thankYou: 'Thank you!',
    complete: 'Your submission is complete.',
  },
  validation: {
    REQUIRED: 'This field is required',
    INVALID_TYPE: 'Invalid value',
    INVALID_EMAIL: 'Invalid email format',
    INVALID_PHONE: 'Invalid phone number',
    INVALID_DATE: 'Enter a valid date (YYYY-MM-DD)',
    MIN_LENGTH: 'Must be at least {{min}} characters',
    MAX_LENGTH: 'Must be at most {{max}} characters',
    PATTERN: 'Value does not match the required format',
    MIN_VALUE: 'Must be at least {{min}}',
    MAX_VALUE: 'Must be at most {{max}}',
    MIN_DATE: 'Date must be on or after {{minDate}}',
    MAX_DATE: 'Date must be on or before {{maxDate}}',
    INVALID_OPTION: 'Select a valid option',
    DUPLICATE_OPTION: 'Duplicate selections are not allowed',
    MIN_SELECTIONS: 'Select at least {{min}} option(s)',
    MAX_SELECTIONS: 'Select at most {{max}} option(s)',
    INVALID_ADDRESS: 'Please choose an address',
    INVALID_BARCODE: 'Invalid barcode format',
    MAX_FILES: 'At most {{max}} file(s) allowed',
    MAX_FILE_SIZE: 'The file is too large',
    INVALID_MIME_TYPE: 'This file type is not allowed',
    UNKNOWN_FILE: 'Referenced file was not found',
    DUPLICATE_FILE: 'The same file was attached more than once',
    default: 'Invalid value',
  },
  shell: {
    tenantSelector: 'Tenant',
  },
  dashboard: {
    openServices: 'Open services',
    inProgress: 'In progress',
    completed: 'Completed',
    serviceTemplates: 'Service templates',
  },
  audit: {
    when: 'When',
    action: 'Action',
    resource: 'Resource',
    actor: 'Actor',
    ip: 'IP',
    noEntries: 'No audit entries match.',
    filterPlaceholder: 'Filter by action prefix (e.g. form.)',
  },
  forms: {
    newForm: 'New form',
    createTitle: 'Create a form',
    createAndOpen: 'Create and open builder',
  },
  services: {
    newService: 'New service',
    shareLink: 'Share link',
    shareLinkTitle: 'Service share link',
    shareLinkInfo:
      'Anyone with this link can start a new case of this service and complete its external forms. Copy it now — it is shown only once.',
    copyLink: 'Copy link',
    createTitle: 'Create a service',
    createAndConfigure: 'Create and configure',
  },
  instances: {
    searchPlaceholder: 'Search title or reference',
    allStatuses: 'All statuses',
    newInstance: 'New instance',
    createTitle: 'Create a service instance',
  },
  members: {
    invite: 'Invite member',
    invitationSent: 'Invitation sent',
    deactivate: 'Deactivate',
    activate: 'Activate',
    inviteTitle: 'Invite a member',
    emailPlaceholder: 'person@example.com',
    firstName: 'First name',
    inviteHint: 'The person signs in with their organization account; membership is linked by email.',
    sendInvitation: 'Send invitation',
  },
  templates: {
    newTemplate: 'New template',
    archive: 'Archive',
    empty: 'No templates yet — create one to generate documents from completed services.',
    createTitle: 'Create an output template',
    typeHtml: 'HTML (Liquid template)',
    typeDocx: 'Word document (.docx with placeholders)',
    typeCsv: 'CSV + attachments (ZIP)',
    templateHtml: 'Template HTML',
    placeholderHelp:
      'Placeholders: {{ref}}, {{title}}, {{tenant}}, {{field}} or {{fieldShort}} for any submitted field.',
    uploadDocx: 'Upload .docx template',
    uploadDocxFirst: 'Upload a .docx template file first.',
    csvInfo: 'Exports all submitted data as export.csv plus every uploaded file under /files.',
    createTemplate: 'Create template',
  },
  builder: {
    addField: 'Add field',
    fieldType: {
      text: 'Text',
      textarea: 'Text area',
      number: 'Number',
      email: 'Email',
      phone: 'Phone',
      checkbox: 'Checkbox',
      date: 'Date',
      address: 'Address',
      barcode: 'Barcode',
      dropdown: 'Dropdown',
      radio: 'Radio',
      multiselect: 'Multi-select',
      file: 'File',
    },
    dragField: 'Drag {{label}}',
    deleteField: 'Delete {{label}}',
    visibleWhen: 'Visible when',
    requiredWhen: 'Required when',
    fieldSettings: 'Field settings',
    fieldId: 'Field id',
    label: 'Label',
    description: 'Description',
    placeholder: 'Placeholder',
    required: 'Required',
    searchable: 'Searchable',
    searchableDesc: 'Value is indexed for search',
    options: 'Options',
    validation: 'Validation',
    minLength: 'Min length',
    maxLength: 'Max length',
    pattern: 'Pattern (regex)',
    patternMessage: 'Pattern message',
    min: 'Min',
    max: 'Max',
    minDate: 'Min date',
    maxDate: 'Max date',
    allowedMimeTypes: 'Allowed MIME types',
    maxFileSize: 'Max file size (bytes)',
    maxFiles: 'Max files',
    minSelections: 'Min selections',
    maxSelections: 'Max selections',
    barcodeFormats: 'Barcode formats',
    conditionalLogic: 'Conditional logic',
    mime: {
      pdf: 'PDF (application/pdf)',
      png: 'PNG image (image/png)',
      jpeg: 'JPEG image (image/jpeg)',
      webp: 'WebP image (image/webp)',
      plain: 'Plain text (text/plain)',
      csv: 'CSV (text/csv)',
      docx: 'Word (.docx)',
      xlsx: 'Excel (.xlsx)',
      pptx: 'PowerPoint (.pptx)',
      doc: 'Word 97-2003 (.doc)',
    },
    conditionFieldPlaceholder: 'Field',
    conditionValuePlaceholder: 'Value',
    condFieldAria: '{{title}}: field',
    condOperatorAria: '{{title}}: operator',
    condValueAria: '{{title}}: value',
    op: {
      equals: 'equals',
      notEquals: 'not equals',
      contains: 'contains',
      greaterThan: 'greater than',
      lessThan: 'less than',
      isEmpty: 'is empty',
      isNotEmpty: 'is not empty',
    },
    optionN: 'Option {{n}}',
    optionValueAria: 'Option {{n}} value',
    optionLabelAria: 'Option {{n}} label',
    removeOptionAria: 'Remove option {{n}}',
    valuePlaceholder: 'value',
    labelPlaceholder: 'label',
    addOption: 'Add option',
    schemaProblems: 'Schema problems',
    confirmDelete: 'Delete field "{{label}}" ({{id}})?',
    noFieldsYet: 'No fields yet. Add one from the palette on the left.',
    selectFieldToConfigure: 'Select a field to configure it.',
    published: 'Published',
    formVersionLive: 'The form version is now live.',
    serviceVersionLive: 'The service version is live.',
    platformDefault: 'Platform default',
    platformDefaultReadonly: 'Platform default (read-only)',
    publishVersion: 'Publish version',
    saveDraft: 'Save draft',
    tabBuilder: 'Builder',
    tabPreview: 'Preview',
    tabVersions: 'Versions',
    livePreview: 'Live preview — exactly what users will see, including conditional logic.',
    colVersion: 'Version',
    colStatus: 'Status',
    colFields: 'Fields',
    colCreated: 'Created',
    colPublished: 'Published',
    addPublishedForm: 'Add a published form',
    noMoreForms: 'No more published forms available',
    pickForm: 'Pick a form',
    addForm: 'Add form',
    colOrder: 'Order',
    colForm: 'Form',
    colCompletion: 'Completion',
    updateToLatest: 'Update to latest',
    completionInternal: 'Internal',
    completionExternal: 'External (link)',
    completionBoth: 'Both',
    addFormEmpty: 'Add at least one published form to this service.',
  },
  renderer: {
    address: {
      manualPlaceholder: 'Enter the address',
      searchPlaceholder: 'Search for an address',
      searchUnavailable: 'Address search is unavailable here — enter the address manually.',
      noMatches: 'No matches found.',
      clear: 'Clear address',
      pickOnMap: 'Pick on map',
      reverseLookup: 'Looking up the address for the selected point…',
    },
    barcode: {
      scan: 'Scan',
      scanTitle: 'Scan barcode',
      cameraDenied: 'Camera access was denied. Allow camera permission in your browser, or type the code manually.',
      cameraNotFound: 'No usable camera was found on this device. You can type the code manually.',
      cameraInUse: 'The camera is already in use by another application. You can type the code manually.',
      cameraUnavailable: 'Camera scanning is not available in this browser. You can type the code manually.',
      cameraNotSupported: 'Camera scanning is not supported in this browser. You can type the code manually.',
      noCamera: 'No camera was found on this device. You can type the code manually.',
      cameraUnavailableTitle: 'Camera unavailable',
      cameraLabel: 'Camera',
      cameraNumber: 'Camera {{number}}',
      pointHint: 'Point the camera at the barcode. The value is filled in automatically once detected.',
    },
    file: {
      uploadFailed: 'Upload failed',
      uploadError: 'The file could not be uploaded.',
      remove: 'Remove {{name}}',
      attach: 'Attach file',
      download: 'Download {{name}}',
      preview: 'Preview {{name}}',
      previewFailed: 'Preview failed',
      previewError: 'The file could not be opened.',
      downloadFailed: 'Download failed',
      downloadError: 'The file could not be downloaded.',
      maxReached: 'Maximum of {{max}} file(s) reached.',
      uploadsUnavailable: 'File uploads are not available here.',
    },
  },
  platform: {
    createTenant: 'Create tenant',
    colName: 'Name',
    colSlug: 'Slug',
    colStatus: 'Status',
    colCreated: 'Created',
    colUpdated: 'Updated',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    delete: 'Delete…',
    confirmDelete: 'Archive and schedule deletion of "{{name}}"?',
    createTenantModalTitle: 'Create a tenant',
    slugDescription: 'Lowercase letters, digits and hyphens',
    form: 'form',
    service: 'service',
    forms: 'forms',
    services: 'services',
    catalogAlert:
      'Platform defaults are visible read-only in every tenant. Create and publish them here, then <b>provision</b> a published default into selected tenants as an editable {{kind}}.',
    newDefault: 'New default {{kind}}',
    provisionToTenants: 'Provision to tenants',
    noDefaults: 'No platform default {{kind}} yet — create one to get started.',
    createAndOpenBuilder: 'Create and open builder',
    provisionModalTitle: 'Provision "{{name}}"',
    targetTenants: 'Target tenants',
    provision: 'Provision',
    provisionedTitle: 'Provisioned',
    provisionedMessage: '{{name}} copied to {{count}} tenant(s).',
    catalogTitle: 'Platform default catalog',
    tabForms: 'Default forms',
    tabServices: 'Default services',
    allTenants: 'All tenants',
    actionPrefix: 'Action prefix',
  },
  search: {
    title: 'Search',
    placeholder: 'Search services, submitted answers and document contents…',
    all: 'All',
    services: 'Services',
    submissions: 'Submissions',
    documents: 'Documents',
    typeService: 'Service',
    typeSubmission: 'Submission',
    typeDocument: 'Document',
    untitled: '(untitled)',
    noResults: 'No results for “{{query}}”.',
  },
  comments: {
    title: 'Comments',
    empty: 'No comments yet.',
    placeholder: 'Write a comment…',
    post: 'Post comment',
    posted: 'Comment posted',
    delete: 'Delete comment',
  },
  import: {
    title: 'Import a service',
    subtitle: 'Create a full service and its forms from a single JSON file — generate it with AI, then import.',
    aiTitle: 'Generate with AI',
    aiHelp: 'Copy this prompt into Claude, describe the service you need, and paste the JSON it returns below.',
    copyPrompt: 'Copy prompt',
    promptCopied: 'Prompt copied to clipboard',
    jsonTitle: 'Import JSON',
    jsonPlaceholder: 'Paste the service JSON here…',
    loadFile: 'Load .json file',
    importButton: 'Import service',
    invalidJson: 'The JSON could not be parsed. Check the format and try again.',
    success: 'Service imported as a draft — review and publish it.',
  },
};

export type Resources = typeof en;
